import { AppBar, Box, Button, Grid, useTheme } from "@mui/material";
import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EmptyList,
	EnhancedSnackbar,
	RouterLinkButton,
} from "@websential/cosmic-v20";
import {
	CLIENT_LIST_SCREEN_SLUG,
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { FormProvider, useForm } from "react-hook-form";
import { IResponse, IServerError } from "../../models/common";
import { Link, useNavigate } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { formatDate, sortDDLByLabel, useNavigateToLogout } from "../../common/Common";
import {
	setSelectedActivityLogActions,
	setSelectedDrawerMenu,
	setSelectedStickyNavBar,
} from "../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AdminClientRoutesComponent } from "../RoleBasedLeftMenu/routes/AdminClientRoutes";
import { ClientRoutesComponent } from "../RoleBasedLeftMenu/routes/ClientRoutes";
import { CountryService } from "../../services/common/CountryService";
import { IAppState } from "../../store/reducers";
import { IGetListRequest } from "../../models";
import { IWSIBClass } from "../../models/WSIBClass";
import { PrimeDataGrid } from "../PrimeDataGrid";
import { WSIBClassHeader } from "./WSIBClassHeader";
import { WSIBClassListDataGridTable } from "./WSIBClassListDataGridTable/WSIBClassListDataGridTable";
import { WSIBClassService } from "../../services/WSIBClassService";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";

export interface IWSIBClassList { }
const WSIB_CLASS_LIST_QUERY_KEY = "wsib-class-list";

const useWSIBClasses = (
	runGetListAPICall: boolean,
	shortName: string,
	label: string,
	description: string,
	provinceId: string,
	parentId: string,
	effectiveDate: String | null,
	endDate: string | null,
	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [

			WSIB_CLASS_LIST_QUERY_KEY,
			runGetListAPICall,
			shortName,
			label,
			description,
			provinceId,
			parentId,
			effectiveDate,
			endDate,
			params,
			navigateToLogout
		],
		queryFn: (data) => {

			return WSIBClassService.getWSIBClass(
				runGetListAPICall,

				shortName,
				label,
				description,
				provinceId,
				parentId,
				effectiveDate,
				endDate,
				params,
				navigateToLogout
			);
		},
		refetchOnWindowFocus: false,
	});
const useWSIBClassParentDDL = (
	provinceId: string
) =>
	useQuery({
		queryKey: ["parent-ddl", provinceId],
		queryFn: (data) => {

			return WSIBClassService.getWSIBClassParentDDL(provinceId);
		},
		refetchOnWindowFocus: false,
	});
const useGetCountryDDL = () =>
	useQuery({
		queryKey: ["country-ddl"],
		queryFn: (data) => {
			return CountryService.getCountryDDL();
		},
		refetchOnWindowFocus: false,
	});

export const WSIBClassList: React.FC<IWSIBClassList> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const isAdminUser = useSelector(
		(state: IAppState) => state.user.profile.isSuperStatus
	);
	const clientIdStore = useSelector((state: IAppState) => state.client.id);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const navigateToLogout = useNavigateToLogout();
	const { AdminClientRoutes } = AdminClientRoutesComponent();
	const { ClientRoutes } = ClientRoutesComponent();

	// for delete row Purpose
	const queryClient = useQueryClient();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [isLoadingProvinceDDL, setIsLoadingProvinceDDL] = useState(false);
	const [selectedRowsValues, setSelectedRowsValues] = useState([]);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "label",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const { id } = useSelector((state: IAppState) => state.accountant);

	const [openDialog, setDialogOpen] = useState(false);
	const [selectedIds, setSelectedIds] = useState([]);
	const [statusValue, setStatusValue] = useState("");
	const [apiAction, setApiAction] = useState("");
	const [emptyList, setEmptyList] = useState(false);
	const [searchText, setSearchText] = useState({});
	const [zeroWSIBClass, setzeroWSIBClass] = useState(true);
	const [wsibClassListUpdatedItems, setWSIBClassListUpdatedItems] =
		useState<any>(undefined);

	// for delete row
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [wsibClassId, setWSIBClassId] = useState<any>(0);

	// Bulk Delete
	const [bulkDelete, setBulkDelete] = useState<boolean>(false);
	const [bulkWSIBClassId, setBulkWSIBClassId] = useState<any>({});
	const [bulkWSIBClassDelete, setBulkWSIBClassDelete] =
		useState<boolean>(false);
	const [isLoading, setIsLoading] = useState(false);
	const [runGetListAPICall, setRunGetListAPICall] =
		useState<any>(true);

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "label",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
		setRunGetListAPICall(true)
	}, []);




	const formMethods = useForm<any>({
		mode: "onBlur",
		reValidateMode: "onChange",
	});


	const { isPending: isPendingCountryDDL, data: countryDDL } = useGetCountryDDL();


	const shortName = formMethods.getValues("shortName");
	const label = formMethods.getValues("label");
	const description = formMethods.getValues("description");
	const provinceId = formMethods.watch("provinceId");
	const parentId = formMethods.getValues("parentId");
	const effectiveDate = formMethods.getValues("effectiveDate");
	const endDate = formMethods.getValues("endDate");

	const { isPending, data } = useWSIBClasses(
		runGetListAPICall,
		shortName,
		label,
		description,
		provinceId,
		parentId,
		formatDate(effectiveDate),
		formatDate(endDate),
		queryParams,
		navigateToLogout
	);
	const wsibClassList: any | null = data;
	

	useEffect(() => {
		if (wsibClassList) {
			setWSIBClassListUpdatedItems(wsibClassList)
		}
	}, [wsibClassList])

	const { isPending: isPendingWSIbClassParentDDL, data: wsibClassParentDDL } = useWSIBClassParentDDL(provinceId);
	// on apply button click api call wsib class list 
	const wsibClassListDataAPICall = (
		shortName: string,
		label: string,
		description: string,
		provinceId: string,
		parentId: string,
		effectiveDate: String,
		endDate: string,
	) => {
		const fetchData = async () => {
			setIsLoading(true);

			try {
				const response: any = await WSIBClassService.getWSIBClass(
					true,
					shortName,
					label,
					description,
					provinceId,
					parentId,
					formatDate(effectiveDate),
					formatDate(endDate),
					queryParams,
					navigateToLogout
				);

				if (response) {
					setWSIBClassListUpdatedItems(response);
				}
			} catch {
			} finally {
				setIsLoading(false);
			}
		};
		fetchData();
	};

	const onSubmit = (values: any) => {
	
		wsibClassListDataAPICall(
			values.shortName,
			values.label,
			values.description,
			values.provinceId,
			values.parentId,
			values.effectiveDate,
			values.endDate,
		);
	};


	const onSearchButtonClicked = () => {
		setQueryParams(searchText);
		setzeroWSIBClass(false);
	};

	const onRowEditClicked = useCallback((params: IWSIBClass) => {
		let path = `/workplace-safety/class/setup/${params.id}`;
		navigate(path);
	}, []);

	const onCloseHandler = () => {
		setDialogOpen(false);
	};

	const callAction = () => {
		if (apiAction === "delete") {
			alert(`Delete row with ID: ${selectedIds} has been clicked!`);
			// TODO api call for delete
		} else if (apiAction === "updateStatus") {
			alert(`action need to trigger : ${apiAction}`);
			// TODO api call for update status
		}
		setDialogOpen(false);
	};

	// For Delete Row
	const deleteWSIBClassField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				wsibClassId: wsibClassId,
				navigateToLogout: navigateToLogout,
			};
			deleteWSIBClassById(delPayload);
			setDialogOpen(false);
		}
	};

	const onRowDeleteClicked = useCallback((params: IWSIBClass) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setApiAction("delete");
		setWSIBClassId(params.id);
	}, []);

	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteWSIBClassById } = useMutation({
		mutationFn: WSIBClassService.delWSIBClass,

		onSuccess: (res: string | IServerError | IResponse | any) => {

			if (typeof (res) === 'string') {
				// remove the cache of the Year fields since we just added a new field.
				setSnackbarData({
					open: true,
					message: res,
					severity: "success",
				});

				return queryClient.invalidateQueries({
					queryKey: [WSIB_CLASS_LIST_QUERY_KEY],
				});
			} else {
				setSnackbarData({
					open: true,
					message: res.message ? res.message : "Something went wrong.",
					severity: res.status === false ? "error" : "success",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
		setStatusValue(event.target.value as string);
		setApiAction("updateStatus");
		setDialogOpen(true);
	};

	// const onKeyFieldClickHandler = (clickedRow: any) => {
	// dispatch(setSelectedWSIBClass(clickedRow));
	// dispatch(setSelectedWSIBClassName(clickedRow));
	// };

	//bulk delete

	const { isPending: isBulkDeleting, mutate: deleteBulkWSIBClasses } =
		useMutation({
			mutationFn: WSIBClassService.delBulkWSIBClasses,
			onSuccess: (res: any | string | IServerError | IResponse) => {
				if (typeof res === "object") {
					// remove the cache of the Year fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong.",
						severity: res.status === false ? "error" : "success",
					});

					setBulkWSIBClassDelete(false);
					return queryClient.invalidateQueries({
						queryKey: [WSIB_CLASS_LIST_QUERY_KEY],
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				setBulkWSIBClassDelete(false);
			},
			onError: (err: any) => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: err.message,
					severity: "error",
				});
				setBulkWSIBClassDelete(false);
			},
		});

	// For Bulk Delete
	const deleteBulkWSIBClassField = () => {
		if (bulkDelete) {
			setBulkDelete(false);
			let payload = {
				wsibClassIds: bulkWSIBClassId,
				navigateToLogout: navigateToLogout,
			};
			deleteBulkWSIBClasses(payload);
			setDialogOpen(false);
		}
	};

	const onRowMultipleDeleteClicked = (params: any) => {

		let bulkDeletePayload = {
			wsibClassIds: { wsibClassIds: params },
		};
		setDialogOpen(true);
		setBulkDelete(true);
		setApiAction("delete");
		setBulkWSIBClassId(bulkDeletePayload);
		setBulkWSIBClassDelete(true);
	};

	const selectionToolbar = useCallback((selectedItems: any) => {
		setSelectedIds(selectedItems);

		// if (selectedItems.length) {
		// 	return (
		// 		<Box sx={{ marginTop: theme.spacing(1) }}>
		// 			<Button
		// 				onClick={() => onRowMultipleDeleteClicked(selectedItems)}
		// 				variant="contained"
		// 				color="error"
		// 			>
		// 				Delete
		// 			</Button>
		// 		</Box>
		// 	);
		// }
		return (
			// <Box sx={{ marginTop: theme.spacing(1) }}>
			// 	<RouterLinkButton
			// 		to="/company/wsibClass/setup"
			// 		text="Add WSIBClass"
			// 	/>
			// </Box>
			<></>
		);
	}, []);

	const onProvinceSelectionChangeHandler = (e: any) => {
		setRunGetListAPICall(false)
		formMethods.setValue('parentId', undefined)
	}

	const upadtedWSIBClassListItems = useMemo(() => {
		return (
			wsibClassListUpdatedItems &&
			wsibClassListUpdatedItems.items &&
			wsibClassListUpdatedItems?.items.map((item: any) => ({
				...item,

			}))
		);
	}, [wsibClassListUpdatedItems]);

	

	const expandedLabels = ["Workplace Safety", "Class"];
	const expandedWSIBClassListOrSetupItem = "List"; // Special case for "Setup" under "City Tax"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Class" &&
				item.label === expandedWSIBClassListOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: parentLabel === "Class" && item.label === expandedWSIBClassListOrSetupItem && "/workplace-safety/class/list",
			};
			return enhancedItem;
		});
	};
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: CLIENT_LIST_SCREEN_SLUG,
				accountantName: "",
				activityType: "activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: false }));


		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	const image =
		"https://taxslips.blob.core.windows.net/taxslips-public-images-container/empty_wsibClass_list.png";

	return (
		// wsibClassList &&
		// 	wsibClassList.items &&
		// 	wsibClassList.items.length === 0 &&
		// 	zeroWSIBClass === true ? (
		// 	<EmptyList
		// 		showImage={true}
		// 		imagePath={image}
		// 		heading={`There's nothing! Add WSIBClass to proceed`}
		// 		content={`You can add WSIBClass by directly clicking on add WSIBClass button.`}
		// 		testId={"wsibClass-empty-list"}
		// 		showAddButton={true}
		// 		addButtonLabel={"Add WSIBClass"}
		// 		navigateToAdd={"/company/wsibClass/setup"}
		// 		showImportButton={false}
		// 		showImportXmlButton={false}
		// 	/>
		// ) : (
		<>
			<div
				style={{
					margin: "15px 0",
					fontSize: "1.5rem",
					fontWeight: "bold",
					fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
				}}
			>
				{"Class List"}
			</div>

			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<WSIBClassHeader
						wsibCLassParentDDL={ wsibClassParentDDL&& sortDDLByLabel(wsibClassParentDDL)}
						countryDDL={countryDDL && countryDDL}
						onProvinceSelectionChangeHandler={onProvinceSelectionChangeHandler}
						setStateRunGetListAPICall={(value: boolean) => setRunGetListAPICall(value)}
						setIsLoadingProvinceDDL={(value: boolean) => setIsLoadingProvinceDDL(value)}
					/>
				</form>
			</FormProvider>

			<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
				<BackdropCircularProgress
					open={
						isPending || isLoading || isPendingWSIbClassParentDDL || isLoadingProvinceDDL || isPendingCountryDDL
					}
					testId="wsib-class-list"
				/>

				<DeleteConfirmationDialog
					open={openDialog}
					deleteActionHandler={
						bulkWSIBClassDelete === true
							? deleteBulkWSIBClassField
							: deleteWSIBClassField
					}
					cancelActionHandler={onCloseHandler}
					testId="wsib-class-list-confirmation-dialog"
				/>
				<WSIBClassListDataGridTable
					columns={wsibClassListUpdatedItems ? wsibClassListUpdatedItems.headerColumns : wsibClassList?.headerColumns ?? []}
					rows={
						wsibClassListUpdatedItems && upadtedWSIBClassListItems
							? upadtedWSIBClassListItems
							: wsibClassList?.items ?? []
						// wsibClassList?.items ?? []
					}
					showIndexColumn={true}
					showChecboxColumn={false}
					showSettingColumn={true}
					onRowEditClicked={onRowEditClicked}
					onRowDeleteClicked={onRowDeleteClicked}
					isLoading={isPending || isDeleting || isBulkDeleting}
					onSnackbarMessageData={(value: any) => setSnackbarData(value)}
					totalRecords={
						wsibClassListUpdatedItems && wsibClassListUpdatedItems.total ? wsibClassListUpdatedItems.total : 0
					}
					initialPageSize={INITIAL_PAGE_SIZE}
					rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
					onRequestParamsChange={onRequestParamsChange}
					selectionToolbar={selectionToolbar}
					selectedRowsValues={(value: any) => setSelectedRowsValues(value)}
					// searchFieldPlaceholder={"Search by Name..."}
					changeLoginForGetEmptyItemsBySearchQuery={(value: any) =>
						setzeroWSIBClass(value)
					}
				// pageTitle="WSIB Class List"
				/>


				{wsibClassList ? (
					<AppBar
						position="sticky"
						color="default"
						elevation={1}
						sx={{
							backgroundColor: "#F7F7F7",

							boxShadow: "none",
							alignItems: "center",
							zIndex: 0,
							position: "sticky",
							bottom: 0,
						}}
					></AppBar>
				) : (
					<></>
				)}

			</Grid>
			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`snackbar`}
				open={snackbarData.open}
			/>
		</>
	);
};
